import { captureException } from '@sentry/react';
import { SplitFactory } from '@splitsoftware/splitio';
import cuid from 'cuid';
import React, { PropsWithChildren, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { config } from 'src/config';
import { isServer } from 'src/shared/helpers/environment';
import { useNonce } from 'src/shared/hooks';
import { Provider, SplitContext } from 'src/shared/hooks/useSplit';

const generateAnonymousId = () => `anonymous-${cuid()}`;

interface SplitProps extends PropsWithChildren<{}> {}

const Split = ({ children }: SplitProps) => {
  const [splitFactory, userId] = useMemo(() => {
    if (!config.SPLIT_API_KEY) return [null, ''];

    const key = isServer() ? generateAnonymousId() : window.splitUserId;

    const factory = SplitFactory({
      core: {
        authorizationKey: config.SPLIT_API_KEY,
        key,
      },
    });

    return [factory, key];
  }, []);

  const [client, setClient] = useState(splitFactory?.client());
  const nonce = useNonce();

  const getClient = async () => {
    try {
      await client?.ready();
    } catch (error) {
      captureException(error);
    }

    return client;
  };

  const updateUserId = (id: string) => {
    if (splitFactory?.client) {
      setClient(splitFactory.client(id));
    }
  };

  const contextValue: SplitContext = {
    client: getClient(),
    updateUserId,
  };

  return (
    <>
      {isServer() && (
        <Helmet>
          <script nonce={nonce}>{`window.splitUserId = '${userId}'`}</script>
        </Helmet>
      )}
      <Provider value={contextValue}>{children}</Provider>
    </>
  );
};

export { Split };
